import { useMemo } from 'preact/hooks';
import { useLocation } from 'preact-iso';
import { useAsync } from '../hooks/useAsync';
import { api } from '../api/client';
import type { ListPlantsResponse } from '../api/types';
import {
  activeAlarmsSignal,
  activeCountSignal,
  hasActiveAlarmSeveritySignal,
} from '../realtime/alarmsStore';
import './AlarmIndicator.css';

/**
 * Global alarm indicator for the AppShell top bar. Concept C.6: the indicator
 * is always visible, regardless of whether the Alarm-Widget is on the current
 * dashboard. Feeds directly off `activeCountSignal`, so it re-renders on
 * every push without any extra plumbing.
 *
 * Tone selection:
 *  - 0 active   → muted grey pill (no attention)
 *  - any Alarm  → error tone (loudest)
 *  - only Warns → warn tone
 *
 * The tooltip lists the top-3 affected plant names; plants are looked up
 * lazily via `/api/plants` (the same call every other page makes — HTTP
 * cache should dedupe it).
 */
export function AlarmIndicator() {
  const location = useLocation();
  const count = activeCountSignal.value;
  const hasAlarmSev = hasActiveAlarmSeveritySignal.value;

  // Plant lookup for the tooltip. Fetched once; refresh follows page loads.
  const plantsState = useAsync(
    (signal) => api.get<ListPlantsResponse>('/api/plants', signal),
    [],
  );

  // `activeAlarms` is deliberately read here (not just via `activeCountSignal`)
  // so the tooltip refreshes when a new alarm arrives even while count stays 0
  // (an Acknowledged row still counts for tooltip purposes since the operator
  // may still want to see which plant it belongs to).
  const active = activeAlarmsSignal.value;

  const plantById = useMemo(() => {
    const map = new Map<string, string>();
    if (plantsState.status === 'ok') {
      for (const p of plantsState.data.plants) map.set(p.id, p.displayName);
    }
    return map;
  }, [plantsState]);

  const topPlants = useMemo(() => {
    const seen = new Set<string>();
    const names: string[] = [];
    for (const a of active) {
      if (a.state === 'Cleared') continue;
      if (seen.has(a.plantId)) continue;
      seen.add(a.plantId);
      names.push(plantById.get(a.plantId) ?? a.plantId);
      if (names.length >= 3) break;
    }
    return names;
  }, [active, plantById]);

  const tone: 'muted' | 'warn' | 'error' =
    count === 0 ? 'muted' : hasAlarmSev ? 'error' : 'warn';

  const label = count === 0 ? '0' : String(count);
  const aria =
    count === 0
      ? 'Keine aktiven Alarme'
      : `${count} aktive Alarm${count === 1 ? '' : 'e'}${
          topPlants.length ? ' — ' + topPlants.join(', ') : ''
        }`;

  const tooltip =
    count === 0
      ? 'Keine aktiven Alarme'
      : topPlants.length
        ? `${count} aktiv:\n${topPlants.join('\n')}`
        : `${count} aktiv`;

  return (
    <button
      type="button"
      class={`hv-alarm-ind hv-alarm-ind--${tone}`}
      aria-label={aria}
      title={tooltip}
      onClick={() => location.route('/alarms')}
    >
      <span class="hv-alarm-ind__icon" aria-hidden="true">
        {/* Bell glyph — inline SVG so no extra dependency + honours currentColor. */}
        <svg width="12" height="12" viewBox="0 0 16 16" fill="none">
          <path
            d="M8 1.5a.5.5 0 0 1 .5.5v.6c2.28.24 4 2.17 4 4.5V9l1.2 2H2.3L3.5 9V7.1c0-2.33 1.72-4.26 4-4.5V2A.5.5 0 0 1 8 1.5Zm-2 11.5a2 2 0 1 0 4 0H6Z"
            fill="currentColor"
          />
        </svg>
      </span>
      <span class="hv-alarm-ind__count">{label}</span>
    </button>
  );
}
